/**
 * Identity Admin API
 *
 * Tenant-scoped user and role management endpoints used by admin screens.
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../../../shared/api/baseQuery';
import { fetchAllPages } from '../../../shared/api/paginate';

// IDN-12: getUsers only ever read response.results of a single
// PageNumberPagination page (PAGE_SIZE=20, grace_it/settings/base.py
// REST_FRAMEWORK), and never followed `next` -- any tenant with more than
// 20 users silently lost the rest, both in the admin user list AND every
// owner/assignee picker across the app that reuses useGetUsersQuery (see
// grep of consumers: RiskForm, ControlForm, AISystemForm, change/asset
// create-edit pages, EmergencyAccessModal, etc). `id` is unique
// (User has no natural unique-and-stable field guaranteed indexed for
// pagination other than the pk), so pin ordering to it for deterministic
// paging.
export const fetchAllUsers = (baseQuery, params = {}) =>
  fetchAllPages(
    baseQuery,
    '/api/v1/auth/users/',
    { ordering: 'id', ...params },
    { pageSize: 500, maxPages: 20 }
  );

export const identityAdminApi = createApi({
  reducerPath: 'identityAdminApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['TenantUsers', 'TenantRoles', 'WorkflowRoleAliases', 'ServiceApiKeys', 'MfaPolicy'],
  endpoints: (builder) => ({
    getUsers: builder.query({
      async queryFn(arg, _queryApi, _extraOptions, baseQuery) {
        const result = await fetchAllUsers(baseQuery, arg?.params);
        if (result.error) {
          return { error: result.error };
        }
        return { data: result.data };
      },
      providesTags: ['TenantUsers'],
    }),

    getRoles: builder.query({
      query: () => ({ url: '/api/v1/auth/roles/' }),
      transformResponse: (response) => response?.results || response || [],
      providesTags: ['TenantRoles'],
    }),

    // GAP-ALIAS-UI: which roles each workflow step notifies (SHR-07).
    getWorkflowRoleAliases: builder.query({
      query: () => ({ url: '/api/v1/auth/workflow-role-aliases/' }),
      providesTags: ['WorkflowRoleAliases'],
    }),

    updateWorkflowRoleAliases: builder.mutation({
      query: (overrides) => ({
        url: '/api/v1/auth/workflow-role-aliases/',
        method: 'PUT',
        body: { overrides },
      }),
      invalidatesTags: ['WorkflowRoleAliases'],
    }),

    getPermissions: builder.query({
      query: () => ({ url: '/api/v1/auth/permissions/' }),
      transformResponse: (response) => response?.results || response || [],
    }),

    createRole: builder.mutation({
      query: (data) => ({
        url: '/api/v1/auth/roles/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['TenantRoles'],
    }),

    updateRole: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: `/api/v1/auth/roles/${id}/`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: ['TenantRoles'],
    }),

    // IDN-29: RoleViewSet (apps/identity/views.py) already supports DELETE
    // -- the admin UI had no way to call it.
    deleteRole: builder.mutation({
      query: (id) => ({
        url: `/api/v1/auth/roles/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['TenantRoles'],
    }),

    bulkUpdateUsers: builder.mutation({
      query: ({ userIds, operation, value }) => ({
        url: '/api/v1/auth/users/bulk-update/',
        method: 'POST',
        body: { user_ids: userIds, operation, value },
      }),
      invalidatesTags: ['TenantUsers'],
    }),

    grantEmergencyAccess: builder.mutation({
      query: ({ userId, role, durationHours, justification }) => ({
        url: '/api/v1/auth/users/emergency-access/',
        method: 'POST',
        body: { user_id: userId, role, duration_hours: durationHours, justification },
      }),
    }),

    updateUser: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: `/api/v1/auth/users/${id}/`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: ['TenantUsers'],
    }),
    // IDN-22 / D12: DELETE users/{id}/ now *deactivates* (keeps data, revokes
    // the user's sessions); reactivate undoes it.
    deactivateUser: builder.mutation({
      query: (id) => ({ url: `/api/v1/auth/users/${id}/`, method: 'DELETE' }),
      invalidatesTags: ['TenantUsers'],
    }),
    reactivateUser: builder.mutation({
      query: (id) => ({ url: `/api/v1/auth/users/${id}/reactivate/`, method: 'POST' }),
      invalidatesTags: ['TenantUsers'],
    }),
    // IDN-22 / D12: tenant service API keys. The list never contains the
    // secret; issue returns it exactly once (`api_key`).
    getApiKeys: builder.query({
      query: () => ({ url: '/api/v1/auth/api-keys/' }),
      transformResponse: (response) => response?.results || [],
      providesTags: ['ServiceApiKeys'],
    }),
    issueApiKey: builder.mutation({
      query: ({ name, expires_in_days }) => ({
        url: '/api/v1/auth/api-keys/issue/',
        method: 'POST',
        body: { name, expires_in_days },
      }),
      invalidatesTags: ['ServiceApiKeys'],
    }),
    revokeApiKey: builder.mutation({
      query: (keyId) => ({
        url: '/api/v1/auth/api-keys/revoke/',
        method: 'POST',
        body: { key_id: keyId },
      }),
      invalidatesTags: ['ServiceApiKeys'],
    }),
    // IDN-22 / D12: MFA policy. The require_mfa switch itself stays in
    // General Settings (single writer, GAP-2FA); this screen only edits the
    // grace period and shows whether MFA is enforced right now.
    getMfaPolicy: builder.query({
      query: () => ({ url: '/api/v1/auth/mfa/policy/' }),
      providesTags: ['MfaPolicy'],
    }),
    updateMfaGracePeriod: builder.mutation({
      query: (days) => ({
        url: '/api/v1/auth/mfa/policy/',
        method: 'PATCH',
        body: { mfa_grace_period_days: days },
      }),
      invalidatesTags: ['MfaPolicy'],
    }),
  }),
});

export const {
  useGetUsersQuery,
  useGetRolesQuery,
  useGetPermissionsQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
  useBulkUpdateUsersMutation,
  useGrantEmergencyAccessMutation,
  useUpdateUserMutation,
  useGetWorkflowRoleAliasesQuery,
  useUpdateWorkflowRoleAliasesMutation,
  useDeactivateUserMutation,
  useReactivateUserMutation,
  useGetApiKeysQuery,
  useIssueApiKeyMutation,
  useRevokeApiKeyMutation,
  useGetMfaPolicyQuery,
  useUpdateMfaGracePeriodMutation,
} = identityAdminApi;

export default identityAdminApi;
